import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import PageHeader from "@/components/PageHeader";
import Breadcrumbs from "@/components/Breadcrumbs";
import SEO from "@/components/SEO";
import useRevealOnScroll from "@/hooks/useRevealOnScroll";
import { ArrowUpRight } from "lucide-react";
import { insights } from "@/data/insights";

function formatDate(iso) {
  try {
    return new Date(iso).toLocaleDateString("de-DE", {
      day: "2-digit",
      month: "long",
      year: "numeric",
    });
  } catch (e) {
    return iso;
  }
}

export default function Insights() {
  useRevealOnScroll();

  return (
    <div className="bg-black text-white min-h-screen flex flex-col">
      <SEO
        title="Insights | Wagenhofer Capital – Perspektiven aus der Praxis"
        description="Beiträge zu Unternehmensnachfolge, Beteiligungen, Holdingstrukturen, Immobilieninvestments und unternehmerischer Verantwortung – aus der Perspektive von Wagenhofer Capital."
        path="/insights"
      />
      <Navigation />
      <Breadcrumbs items={[{ label: "Insights", to: "/insights" }]} />

      <PageHeader
        testId="page-insights"
        overline="Insights"
        title={
          <>
            Perspektiven
            <br />
            <span className="text-zinc-400">aus der Praxis.</span>
          </>
        }
        subtitle="Strukturierte Gedanken zu Themen, die uns als unternehmerische Beteiligungsgesellschaft beschäftigen – Unternehmensnachfolge, Holdingstrukturen, Immobilien, Mittelstand und langfristiges Wirtschaften."
      />

      <section
        data-testid="section-insights"
        className="section-y border-b border-white/5"
      >
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-white/5">
            {insights.map((article, idx) => (
              <Link
                key={article.slug}
                to={`/insights/${article.slug}`}
                data-testid={`insight-${article.slug}`}
                className="group bg-black hover:bg-[#0a0a0a] transition-colors duration-500 p-8 md:p-10 reveal"
                style={{ transitionDelay: `${idx * 80}ms` }}
              >
                <div className="aspect-[16/10] overflow-hidden mb-8 border border-white/5">
                  <img
                    src={article.image}
                    alt={article.title}
                    title={article.title}
                    loading="lazy"
                    className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="flex items-center gap-3 mb-4 text-xs text-zinc-500 tracking-[0.2em] uppercase">
                  <span className="text-[#D4AF37]">{article.category}</span>
                  <span>·</span>
                  <span>{article.readingTime}</span>
                </div>
                <h2 className="font-display text-xl md:text-2xl tracking-tight font-medium mb-4 leading-snug">
                  {article.title}
                </h2>
                <p className="text-zinc-400 leading-relaxed font-light text-[15px] mb-6">
                  {article.excerpt}
                </p>
                <div className="flex items-center justify-between pt-6 border-t border-white/5">
                  <span className="text-xs text-zinc-500">
                    {formatDate(article.date)}
                  </span>
                  <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.24em] text-[#D4AF37]">
                    Lesen
                    <ArrowUpRight
                      className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                      strokeWidth={1.5}
                    />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
